<script setup lang="ts">
  // Components
  import { computed } from 'vue';
  import DropzoneArea from './DropzoneArea.vue';
  import FileList from './FileList.vue';
  // Composables
  import { useFileDropzone, useImageValidation } from '@/shared/lib';
  import { useImageUploadStore } from '@/features/Document/';

  defineProps<{
    allUploadsComplete: boolean;
    filiais?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'filesSelected', files: File[]): void;
    (e: 'uploadProgress', fileId: string, progress: number): void;
    (e: 'uploadComplete', fileId: string, imageIds?: number[]): void;
    (e: 'allFilesDeleted'): void;
  }>();

  const { fileInput, isDragging, openFileDialog, onDragEnter, onDragLeave, onDragOver, onDrop } =
    useFileDropzone();
  const { filterImageFiles, showError } = useImageValidation();
  const { images, handleImagesSelected, getImageStatus, removeImage, uploadProgress } =
    useImageUploadStore;

  const overallProgress = computed(() => {
    if (!uploadProgress.value || uploadProgress.value.size === 0) {
      return 0;
    }

    let totalProgress = 0;
    let count = 0;

    uploadProgress.value.forEach((progress: number) => {
      totalProgress += progress;
      count++;
    });

    return count > 0 ? Math.round(totalProgress / count) : 0;
  });

  const getImageProgress = () => overallProgress.value;

  const onFilesSelectedHandler = (newFiles: FileList | null) => {
    handleImagesSelected(
      newFiles,
      filterImageFiles,
      showError,
      (selectedFiles: File[]) => emit('filesSelected', selectedFiles as File[]),
      (fileId: string, imageIds?: number[]) => {
        emit('uploadComplete', fileId, imageIds);
      },
      (fileId: string, progress: number) => {
        emit('uploadProgress', fileId, progress);
      }
    );
  };

  const handleDrop = (e: DragEvent) => {
    const droppedFiles = onDrop(e);
    if (droppedFiles) {
      onFilesSelectedHandler(droppedFiles);
    }
  };

  const handleImageDeleted = (file: File) => {
    if (file) {
      const removed = removeImage(file);
      if (removed) {
        emit('allFilesDeleted');
        if (fileInput.value) {
          fileInput.value.value = '';
        }
      }
    }
  };
</script>

<template>
  <div>
    <DropzoneArea
      v-if="images.length === 0"
      :is-dragging="isDragging"
      :on-drag-enter="onDragEnter"
      :on-drag-leave="onDragLeave"
      :on-drag-over="onDragOver"
      :on-drop="handleDrop"
      :open-file-dialog="openFileDialog"
      :multiple="false"
    />

    <FileList
      v-else
      :files="images"
      :get-file-progress="getImageProgress"
      :get-file-status="getImageStatus"
      :filiais="filiais"
      @image-deleted="handleImageDeleted"
    />

    <input
      ref="fileInput"
      type="file"
      multiple
      accept=".png,.svg,image/png,image/svg+xml"
      class="hidden"
      @change="onFilesSelectedHandler(($event.target as HTMLInputElement).files)"
    >
  </div>
</template>

<style scoped>
  .progress-container {
    margin-bottom: 16px;
  }

  .progress-label {
    margin-bottom: 4px;
    font-size: 14px;
  }

  .progress-bar {
    width: 100%;
    height: 8px;
    background-color: #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background-color: #4caf50;
    transition: width 0.3s ease;
  }
</style>
